import { Dispatch, FunctionComponent, useCallback, useState } from 'react';
import { VoucherCheckoutState } from '../vouchersState';
import { EmailField, FormContext, OnSubmit, PhoneField, TextField, useForm } from '@cademy-ui/form';
import Button from '../../../../components/Button';
import styles from './styles.module.scss';
import { MarketingConsentField } from '../../../../components/Checkout/ContactDetailsStep/MarketingConsentField';
import { useIsMarketplace } from '../../../../contexts/AppContext/useIsMarketplace';
import { CademyError } from '@shared/domain-shared';
import { useGetUtms } from '../../../../hooks/useUtms';
import { UTMs } from '@shared/types';
import useCademyError from '../../../../hooks/useCademyError';
import ErrorAlert from '../../../../components/ErrorAlert';

type FormShape = {
    buyerFirstName: string;
    buyerLastName: string;
    buyerEmail: string;
    buyerTelephone: string;
    educatorMarketingConsent: boolean;
};

const createVoucher = async (
    state: VoucherCheckoutState,
    buyerFormValues: Partial<FormShape>,
    utms: UTMs
): Promise<{ voucherId: string; voucherCode: string }> => {
    try {
        const response = await fetch(`${process.env.NEXT_PUBLIC_CADEMY_API_URL}/vouchers`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                recipient: {
                    first_name: state.recipient.firstName,
                    email: state.recipient.email || undefined,
                },
                buyer: {
                    first_name: buyerFormValues.buyerFirstName,
                    last_name: buyerFormValues.buyerLastName,
                    email: buyerFormValues.buyerEmail,
                    phone: buyerFormValues.buyerTelephone,
                    educator_marketing_consent: buyerFormValues.educatorMarketingConsent,
                },
                amount: state.amount,
                educator_slug: state.educator.slug,
                metadata: {
                    message: state.message,
                    sharing_method: state.sharingMethod,
                    educator_slug: state.educator.slug,
                    course_ref: state.courseSlug,
                },
                utm_details: utms,
            }),
        });

        if (!response.ok) {
            if (response.headers.get('content-type') === 'application/problem+json') {
                const problemDetails = await response.json();
                const error = CademyError.fromObject(problemDetails);
                throw error;
            }
            if (response.headers.get('content-type') === 'application/json') {
                const errorDetails = await response.json();
                const error = CademyError.fromUnknown(errorDetails);
                throw error;
            }
            const error = await response.text();
            throw new Error(error);
        }

        const data = await response.json();

        return {
            voucherId: data.voucher_id,
            voucherCode: data.voucher_code,
        };
    } catch (error) {
        console.error(error);
        throw error;
    }
};

export const BuyerDetails: FunctionComponent<{
    state: VoucherCheckoutState;
    dispatch: Dispatch<Partial<VoucherCheckoutState>>;
}> = ({ state, dispatch }) => {
    const { inError, title, detail, setError } = useCademyError();
    const isMarketplace = useIsMarketplace();
    const getUtms = useGetUtms();

    const onSubmit: OnSubmit<FormShape> = useCallback(
        async (formResponse) => {
            try {
                setError(undefined);
                const voucher = await createVoucher(state, formResponse, getUtms());
                dispatch({
                    currentStep: 'payment',
                    voucherId: voucher.voucherId,
                    code: voucher.voucherCode,
                });
            } catch (error) {
                setError(
                    CademyError.fromUnknown(
                        error,
                        new CademyError(
                            '/voucher-error',
                            'Failed to create voucher',
                            undefined,
                            500
                        )
                    )
                );
            }
        },
        [dispatch, getUtms, setError, state]
    );

    const formContext = useForm({
        initialValues: {
            buyerFirstName: '',
            buyerLastName: '',
            buyerEmail: '',
            buyerTelephone: '',
            educatorMarketingConsent: false,
        },
        requiredStrategy: 'required',
        onSubmit: onSubmit,
    });

    return (
        <FormContext.Provider value={formContext}>
            <div className={styles.buyerContainer}>
                <h3>Your contact details</h3>
                <ErrorAlert
                    inError={inError}
                    title={title}
                    detail={detail}
                    className={styles.errorAlert}
                />
                <TextField name="buyerFirstName" label="First Name" required />
                <TextField name="buyerLastName" label="Last Name" required />
                <EmailField name="buyerEmail" label="Email" required />
                <PhoneField name="buyerTelephone" label="Telephone" />
                <MarketingConsentField
                    name="educatorMarketingConsent"
                    origin={isMarketplace ? 'marketplace' : 'landing_page'}
                    educator={state.educator}
                />
            </div>
            <div className={styles.continueButtonContainer}>
                <Button onClick={formContext.submit} preset="primary">
                    Continue
                </Button>
            </div>
        </FormContext.Provider>
    );
};
